import { useQuery } from '@tanstack/react-query';
import type {
  ExternalTestLocation,
  ExternalTestBddContext,
  ExternalTestProvider,
  ExternalTestResultOutput,
  ExternalTestStep,
} from '@chatsdet/types';
import type { StatusName } from '@chatsdet/ui';
import { API_URL } from '../../config';
import { toApiError } from '../../lib/api-error';
import type { ExternalRunProgressSnapshot } from '@chatsdet/types';

/** Where one test sits inside the recording for its attempt. */
export type ItemCaptureDto = {
  /** S3 key under `orgs/{orgId}/…`, served by the existing `/videos/*` route. */
  videoStorageKey: string;
  sessionIndex: number | null;
  videoOffsetMs: number | null;
  videoDurationMs: number | null;
};

export type CaptureSessionDto = {
  sessionIndex: number;
  videoStorageKey: string;
  durationMs: number | null;
  firstFrameWallClockMs: number | null;
};

export type ExternalRunItemArtifactDto = {
  id: string;
  kind: 'report' | 'video' | 'trace' | 'log' | 'screenshot';
  name: string;
  sizeBytes: number | null;
  openPath: string;
};

export type ExternalRunItemDto = {
  id: string;
  testKey: string;
  displayName: string;
  status: 'passed' | 'failed' | 'skipped';
  attempt: number;
  /** Execution order within the run; null for legacy rows. */
  orderIndex: number | null;
  durationMs: number | null;
  /** Per-attempt execution window; null when the provider did not record it. */
  startedAt: string | null;
  finishedAt: string | null;
  /** Resolved Gherkin context for this attempt; null for non-BDD results. */
  bdd: ExternalTestBddContext | null;
  error: { message?: string; stack?: string; type?: string } | null;
  /** Native provider steps for Playwright detail views. */
  nativeSteps?: ExternalTestStep[];
  /** Framework tags (e.g. playwright-bdd's mapped Gherkin `@tags`); null when the provider doesn't report them. */
  tags: string[] | null;
  /** Where the test is defined; null when the provider doesn't report it (only Playwright does today). */
  location: ExternalTestLocation | null;
  /** This attempt's own stdout/stderr/attachments. Kept for FAILED attempts only; null otherwise. */
  output: ExternalTestResultOutput | null;
  /** Null when this attempt was not recorded (attempt 1 never is). */
  capture: ItemCaptureDto | null;
  /** Provider/suite-reported label/value facts about this test (e.g. environment); may be empty. */
  properties: Array<{ label: string; value: string }>;
  /** Stored files for this attempt. `openPath` is an API path under `API_URL`, served with the viewer's auth. */
  artifacts: ExternalRunItemArtifactDto[];
  /** Native workflow conversion for the whole logical scenario, when one exists. */
  convertedWorkflow: {
    workflowId: string;
    state: 'generating' | 'ready' | 'failed';
  } | null;
};

export type ExternalRunDetailDto = {
  id: string;
  runnerId: string | null;
  runnerName: string | null;
  provider: ExternalTestProvider;
  targetUrl: string | null;
  revision: string | null;
  status: 'open' | 'collecting' | 'sealed' | 'canceled';
  /**
   * The run was cut short and its results salvaged from the boundary log, so
   * `items` covers only the tests that got to run — tests that never started
   * have no row at all. A `sealed` run with this set is INCOMPLETE, and the UI
   * must say so: the counts look like a whole suite otherwise.
   */
  resultsTruncated: boolean;
  failureMessage: string | null;
  selection: { specPath: string | null; grep: string | null };
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  items: ExternalRunItemDto[];
  capture: {
    sessions: CaptureSessionDto[];
    /**
     * Which attempt was recorded. Capture only runs on a RETRY, so when that
     * retry passed the video shows a pass — the UI labels it rather than letting
     * a viewer assume they are watching the failure.
     */
    attempt: number | null;
  };
  artifactCapture?: {
    status: 'unknown' | 'not_requested' | 'complete' | 'partial';
    discoveredCount: number;
    storedCount: number;
    omittedCount: number;
    issues: Array<{ code: string; count: number; samplePaths: string[] }>;
  };
  recordings?: {
    status: 'not_requested' | 'preparing' | 'ready' | 'partial';
    expectedCount: number;
    readyCount: number;
  };
};

export type ExternalScenarioOverviewDto = {
  key: string;
  label: string;
  routeItemId: string;
  verdict: 'passed' | 'flaky' | 'failed' | 'skipped';
  total: number;
  /** Provider-native step count for a Playwright leaf scenario, when present. */
  nativeStepCount: number | null;
  durationMs: number | null;
  firstFailureMessage: string | null;
  /** Which area (1-based shard) this scenario ran in; null for pre-area runs. */
  areaIndex: number | null;
};

/**
 * "Area" is the product word for one shard of a run — a directory on a
 * directory-sharded suite, a label partition, or the whole suite on a
 * single-shard run. Never say "shard" in UI copy.
 *
 * The single source of truth for which states exist and their display
 * order — the areas filter, the URL-param validator, and the type itself
 * all derive from this tuple so a new state can't drift between them.
 */
export const RUN_AREA_STATES = [
  'running',
  'queued',
  'completed',
  'failed',
  'canceled',
  'disabled',
] as const;

export type RunAreaState = (typeof RUN_AREA_STATES)[number];

/** Narrows an arbitrary string (e.g. a URL param) to a known `RunAreaState`. */
export function isRunAreaState(value: string): value is RunAreaState {
  return (RUN_AREA_STATES as readonly string[]).includes(value);
}

/**
 * The plain label/status for an area's state. Not read directly by the UI —
 * the areas table's State cell goes through `areaStateBadge` (`areaSummary.ts`),
 * which falls back to this map for every state except a completed area with
 * no results, which it overrides to read "No results"/skipped rather than
 * this map's "Passed"/success (so the table agrees with the areas grid
 * tile's own `areaToSummaryStatus`, which doesn't use this map at all). The
 * areas-grid State *filter* also doesn't use this map — see
 * `AREA_STATE_FILTER_LABEL` in `areaSummary.ts`, which labels by state word
 * rather than outcome.
 */
export const RUN_AREA_STATE_BADGE: Record<RunAreaState, { label: string; status: StatusName }> = {
  queued: { label: 'Queued', status: 'queued' },
  running: { label: 'Running', status: 'running' },
  completed: { label: 'Passed', status: 'success' },
  failed: { label: 'Failed', status: 'failed' },
  canceled: { label: 'Canceled', status: 'canceled' },
  disabled: { label: 'Disabled', status: 'skipped' },
};

export type RunAreaDto = {
  index: number;
  label: string;
  specPath: string | null;
  state: RunAreaState;
  attempt: number;
  total: number;
  passed: number;
  failed: number;
  flaky: number;
  skipped: number;
  durationMs: number | null;
};

/** A re-run this run has spawned (usually a one-area re-run). */
export type RunRerunDto = {
  runId: string;
  status: string;
  createdAt: string;
  selection: { specPath: string | null; grep: string | null };
  /** Which area of THIS run it re-ran, when it resolves to exactly one. */
  areaIndex: number | null;
  total: number;
  passed: number;
  failed: number;
  flaky: number;
  skipped: number;
};

/** Verdict counts over a set of scenarios (the whole run, or one area). */
export type RunSummaryCountsDto = {
  total: number;
  passed: number;
  failed: number;
  flaky: number;
  skipped: number;
  durationMs: number | null;
};

export type ExternalRunOverviewDto = Omit<ExternalRunDetailDto, 'items' | 'capture'> & {
  /**
   * Scoped to `area` when one is requested, else the whole run. `items` is
   * that scope's scenarios — empty for the whole of a multi-area run, whose
   * summary is `areas` instead (thousands of scenarios per poll made the page
   * slow).
   */
  summary: { counts: RunSummaryCountsDto; items: ExternalScenarioOverviewDto[] };
  results: ExternalScenarioOverviewDto[];
  progress: ExternalRunProgressSnapshot | null;
  shardTotal: number | null;
  /** Set when this run is itself a re-run of one area of another run. */
  rerunOf: { runId: string } | null;
  /**
   * The runner's CURRENT disabled-areas config (by `specPath`) — not this
   * run's snapshot. An area's row `state` (`'disabled'`) reflects what THIS
   * run did; this list is what the NEXT run will do, and is what the
   * enable/disable row action reads to decide its label.
   */
  runnerDisabledAreas: string[];
  /** The suite whose runner config the area enable/disable action edits; null for ingest-API runs. */
  suiteId: string | null;
  areas: RunAreaDto[];
  reruns: RunRerunDto[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
};

/**
 * Fetch a single external-suite run with its per-test items. Polls every 3s
 * while the run is still open/collecting (mirrors the native run-detail hook),
 * then stops once the run is sealed or canceled.
 *
 * `page`/`pageSize` are always sent, even in the grouped/whole-run view
 * (`view === 'grouped' && !selectedArea` in `ExternalRunDetail`) where the
 * resulting `results` slice is never rendered — the areas grid reads
 * `areas` instead. Whether the run is "grouped" depends on
 * `areas.length`, which only becomes known from THIS fetch's response, so
 * omitting the params only for grouped mode would need either a second
 * round trip or duplicated pagination state at the call site. The server
 * defaults/clamps `pageSize` to a small page (25), so the unused slice this
 * fetches is cheap; not worth the extra state to avoid it.
 */
export function useExternalRun(
  runId: string | null,
  pagination: {
    page: number;
    pageSize: number;
    q?: string;
    verdicts?: string[];
    /** Narrow `results` (server-paginated) and `summary` to one area. */
    area?: number;
  }
) {
  const verdictsKey = (pagination.verdicts ?? []).join(',');
  return useQuery<ExternalRunOverviewDto>({
    queryKey: [
      'external-run',
      runId,
      pagination.page,
      pagination.pageSize,
      pagination.q ?? '',
      verdictsKey,
      pagination.area ?? null,
    ],
    enabled: Boolean(runId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return (status && status !== 'sealed' && status !== 'canceled') ||
        query.state.data?.recordings?.status === 'preparing'
        ? 3000
        : false;
    },
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(pagination.page),
        pageSize: String(pagination.pageSize),
      });
      if (pagination.q) params.set('q', pagination.q);
      if (pagination.verdicts && pagination.verdicts.length > 0) {
        params.set('verdicts', pagination.verdicts.join(','));
      }
      if (pagination.area != null) params.set('area', String(pagination.area));
      const res = await fetch(`${API_URL}/external-tests/runs/${runId}?${params}`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to load external test run');
      const body = (await res.json()) as {
        ok: boolean;
        data: Omit<ExternalRunOverviewDto, 'pagination'>;
        pagination: ExternalRunOverviewDto['pagination'];
      };
      return { ...body.data, pagination: body.pagination };
    },
  });
}
